import {useEffect, useState} from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Skeleton,
    IconButton,
    Stack,
    Checkbox,
    FormControlLabel,
    FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {BankService, BankCreateBO, BankUpdateBO} from '@/services/manageService/bankService';
import {InputField} from '@/components/common';
import {usePageCrud} from '@/hooks/usePageCrud';

interface Props {
    open: boolean;
    onClose: (payload?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => void;
    bankId?: number;
}

export default function BankDetail({open, onClose, bankId}: Props) {
    const isEdit = !!bankId;
    const {canCreate, canUpdate} = usePageCrud({
        read: ['bank_list', 'bank_get'],
        create: 'admin:bank:create',
        update: 'admin:bank:update',
        remove: 'admin:bank:delete',
    });
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    const [form, setForm] = useState<{
        bankCode: string;
        hkBankCode: string;
        nameCn: string;
        nameEn: string;
        accountPattern: string;
        enabled: number;
    }>({
        bankCode: '',
        hkBankCode: '',
        nameCn: '',
        nameEn: '',
        accountPattern: '',
        enabled: 1,
    });

    const [fieldErrors, setFieldErrors] = useState<{
        bankCode?: string;
        _global?: string;
    }>({});

    const loadDetail = async () => {
        if (!bankId) return;
        try {
            setLoading(true);
            const data = await BankService.getById(bankId);
            setForm({
                bankCode: data.bankCode ?? '',
                hkBankCode: data.hkBankCode ?? '',
                nameCn: data.nameCn ?? '',
                nameEn: data.nameEn ?? '',
                accountPattern: data.accountPattern ?? '',
                enabled: data.enabled ?? 1,
            });
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            if (isEdit) {
                loadDetail();
            } else {
                setForm({bankCode: '', hkBankCode: '', nameCn: '', nameEn: '', accountPattern: '', enabled: 1});
            }
            setFieldErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, bankId]);

    const validate = (): boolean => {
        const errors: typeof fieldErrors = {};
        if (!form.bankCode.trim()) errors.bankCode = 'Bank Code is required';
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            if (isEdit && bankId) {
                const bo: BankUpdateBO = {
                    id: bankId,
                    bankCode: form.bankCode.trim(),
                    hkBankCode: form.hkBankCode,
                    nameCn: form.nameCn,
                    nameEn: form.nameEn,
                    accountPattern: form.accountPattern,
                    enabled: form.enabled,
                };
                await BankService.update(bo);
            } else {
                const bo: BankCreateBO = {
                    bankCode: form.bankCode.trim(),
                    hkBankCode: form.hkBankCode,
                    nameCn: form.nameCn,
                    nameEn: form.nameEn,
                    accountPattern: form.accountPattern,
                    enabled: form.enabled,
                };
                await BankService.create(bo);
            }
            onClose({refreshed: true, message: 'Saved successfully', severity: 'success'});
        } catch (e: any) {
            setFieldErrors({_global: e?.message || 'Save failed'});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose()} fullWidth maxWidth="sm">
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {isEdit ? 'Edit Bank' : 'Create Bank'}
                    <IconButton size="small" onClick={() => onClose()}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Stack spacing={2} mt={1}>
                        {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} variant="rounded" height={40}/>)}
                    </Stack>
                ) : (
                    <Stack spacing={2} mt={1}>
                        <InputField
                            size="small"
                            label="Bank Code (English Abbr.)"
                            required
                            display={isEdit}
                            error={!!fieldErrors.bankCode}
                            helperText={fieldErrors.bankCode || 'e.g. HSBC / SCB / BEA'}
                            value={form.bankCode}
                            onChange={(e) => setForm({...form, bankCode: e.target.value})}
                        />
                        <InputField
                            size="small"
                            label="HK Bank Code"
                            helperText="e.g. 004"
                            value={form.hkBankCode}
                            onChange={(e) => setForm({...form, hkBankCode: e.target.value})}
                        />
                        <InputField
                            size="small"
                            label="Name (CN)"
                            value={form.nameCn}
                            onChange={(e) => setForm({...form, nameCn: e.target.value})}
                        />
                        <InputField
                            size="small"
                            label="Name (EN)"
                            value={form.nameEn}
                            onChange={(e) => setForm({...form, nameEn: e.target.value})}
                        />
                        <InputField
                            size="small"
                            label="Account Pattern"
                            helperText="e.g. XXX-YYY-ZZZZZZZZZ"
                            value={form.accountPattern}
                            onChange={(e) => setForm({...form, accountPattern: e.target.value})}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={form.enabled === 1}
                                    onChange={(e) => setForm({...form, enabled: e.target.checked ? 1 : 0})}
                                />
                            }
                            label="Enabled"
                        />
                        {fieldErrors._global && (
                            <FormHelperText error>{fieldErrors._global}</FormHelperText>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cancel</Button>
                {(isEdit ? canUpdate : canCreate) && (
                <Button variant="contained" onClick={onSave} disabled={saving || loading}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
